/**
 * Documento d'ordine da allegare alla spedizione.
 *
 * Due varianti dello stesso documento:
 *  - "completo": con prezzo unitario, totale riga e totale ordine (tipo DDT);
 *  - "packing list": solo articoli e quantità, senza importi, per il documento
 *    che viaggia dentro il collo quando i valori non devono essere visibili.
 */
import jsPDF from 'jspdf';
import autoTable, { RowInput } from 'jspdf-autotable';
import { EXPORT_CONFIG } from './exportConfig';
import type { DatiAzienda } from '@/services/datiAziendaService';

export interface RigaOrdineStampa {
  codice?: string;
  descrizione: string;
  /** Reparto del movimento: compare in tabella solo se valorizzato su almeno una riga. */
  reparto?: string;
  quantita: number;
  prezzoUnitario: number;
  /** Riga senza articolo (spedizione, imballo, ...): va in tabella ma non conta nei pezzi. */
  voceLibera?: boolean;
}

export interface DestinatarioStampa {
  nome: string;
  indirizzo?: string;
  cap?: string;
  citta?: string;
  provincia?: string;
  partitaIva?: string;
  codiceFiscale?: string;
  telefono?: string;
  email?: string;
}

export interface OrdineStampa {
  numeroOrdine: string;
  data: string;
  destinatario?: DestinatarioStampa;
  righe: RigaOrdineStampa[];
  note?: string;
  /** Se false il documento non riporta prezzi né totali (packing list). */
  conPrezzi: boolean;
  azienda?: DatiAzienda | null;
}

const MARGIN = EXPORT_CONFIG.pdf.marginLeft;

function hexToRgb(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [0, 0, 0];
}

function formatEuro(value: number): string {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
}

function formatData(value: string): string {
  const date = new Date(value);
  return isNaN(date.getTime()) ? value : date.toLocaleDateString('it-IT');
}

/** Riga "CAP Città (PR)" saltando i pezzi mancanti. */
function localita(cap?: string, citta?: string, provincia?: string): string {
  const parts = [cap, citta].filter(Boolean).join(' ');
  return provincia ? `${parts} (${provincia})`.trim() : parts;
}

/** Intestazione con i dati del mittente; ritorna la Y a cui proseguire. */
function addIntestazione(doc: jsPDF, azienda: DatiAzienda | null | undefined): number {
  const { colors } = EXPORT_CONFIG;
  let y = MARGIN;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(...hexToRgb(colors.primary));
  doc.text(azienda?.ragioneSociale || EXPORT_CONFIG.appName, MARGIN, y);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...hexToRgb(colors.textSecondary));

  if (azienda) {
    const riga1 = [azienda.indirizzo, localita(azienda.cap, azienda.citta, azienda.provincia)]
      .filter(Boolean)
      .join(' · ');
    if (riga1) {
      doc.text(riga1, MARGIN, y);
      y += 4.5;
    }

    const fiscali = [
      azienda.partitaIva ? `P.IVA ${azienda.partitaIva}` : null,
      azienda.codiceFiscale ? `C.F. ${azienda.codiceFiscale}` : null,
    ].filter(Boolean).join(' · ');
    if (fiscali) {
      doc.text(fiscali, MARGIN, y);
      y += 4.5;
    }

    const contatti = [azienda.telefono, azienda.email, azienda.sitoWeb].filter(Boolean).join(' · ');
    if (contatti) {
      doc.text(contatti, MARGIN, y);
      y += 4.5;
    }
  }

  // Linea di separazione
  doc.setDrawColor(...hexToRgb(colors.borderColor));
  doc.setLineWidth(0.3);
  doc.line(MARGIN, y + 1, doc.internal.pageSize.getWidth() - MARGIN, y + 1);

  return y + 9;
}

/** Titolo del documento e riquadro destinatario; ritorna la Y a cui proseguire. */
function addTestataDocumento(doc: jsPDF, ordine: OrdineStampa, startY: number): number {
  const { colors } = EXPORT_CONFIG;
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = startY;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...hexToRgb(colors.textPrimary));
  doc.text(ordine.conPrezzi ? 'DOCUMENTO D\'ORDINE' : 'DOCUMENTO DI SPEDIZIONE', MARGIN, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const rifOrdine = `Ordine n. ${ordine.numeroOrdine}`;
  const rifData = `Data ${formatData(ordine.data)}`;
  doc.text(rifOrdine, pageWidth - MARGIN - doc.getTextWidth(rifOrdine), y - 4);
  doc.text(rifData, pageWidth - MARGIN - doc.getTextWidth(rifData), y + 1);

  y += 10;

  // Riquadro destinatario
  const d = ordine.destinatario;
  const boxWidth = (pageWidth - MARGIN * 2) * 0.55;
  const boxX = pageWidth - MARGIN - boxWidth;

  doc.setFontSize(8);
  doc.setTextColor(...hexToRgb(colors.textSecondary));
  doc.text('DESTINATARIO', boxX + 3, y + 1);

  const righeDest: string[] = [];
  if (d) {
    righeDest.push(d.nome);
    if (d.indirizzo) righeDest.push(d.indirizzo);
    const loc = localita(d.cap, d.citta, d.provincia);
    if (loc) righeDest.push(loc);
    const fiscali = [
      d.partitaIva ? `P.IVA ${d.partitaIva}` : null,
      !d.partitaIva && d.codiceFiscale ? `C.F. ${d.codiceFiscale}` : null,
    ].filter(Boolean).join(' · ');
    if (fiscali) righeDest.push(fiscali);
    const contatti = [d.telefono, d.email].filter(Boolean).join(' · ');
    if (contatti) righeDest.push(contatti);
  } else {
    righeDest.push('—');
  }

  const boxHeight = 6 + righeDest.length * 4.8;
  doc.setDrawColor(...hexToRgb(colors.borderColor));
  doc.setLineWidth(0.3);
  doc.rect(boxX, y - 4, boxWidth, boxHeight);

  let dy = y + 5;
  righeDest.forEach((riga, index) => {
    doc.setFont('helvetica', index === 0 ? 'bold' : 'normal');
    doc.setFontSize(index === 0 ? 10 : 9);
    doc.setTextColor(...hexToRgb(index === 0 ? colors.textPrimary : colors.textSecondary));
    doc.text(riga, boxX + 3, dy);
    dy += index === 0 ? 5.2 : 4.6;
  });

  return Math.max(dy, y + boxHeight) + 6;
}

/** Tabella delle righe; ritorna la Y finale. */
function addRighe(doc: jsPDF, ordine: OrdineStampa, startY: number): number {
  const { colors, pdf } = EXPORT_CONFIG;
  const pageWidth = doc.internal.pageSize.getWidth();
  const tableWidth = pageWidth - MARGIN * 2;

  // Il reparto occupa spazio solo sui documenti che lo valorizzano.
  const conReparto = ordine.righe.some((r) => r.reparto);

  interface ColonnaOrdine {
    header: string;
    valore: (riga: RigaOrdineStampa) => string;
    /** Peso relativo: le quote vengono normalizzate sulla larghezza disponibile. */
    quota: number;
    halign?: 'left' | 'center' | 'right';
  }

  const colonne: ColonnaOrdine[] = [
    { header: 'Codice', valore: (r) => r.codice || '', quota: 14 },
    { header: 'Descrizione', valore: (r) => r.descrizione, quota: 42 },
  ];

  if (conReparto) {
    colonne.push({ header: 'Reparto', valore: (r) => r.reparto || '', quota: 20 });
  }

  colonne.push({ header: 'Q.tà', valore: (r) => String(r.quantita), quota: 10, halign: 'center' });

  if (ordine.conPrezzi) {
    colonne.push(
      {
        header: 'Prezzo unit.',
        valore: (r) => formatEuro(r.prezzoUnitario),
        quota: 17,
        halign: 'right',
      },
      {
        header: 'Totale',
        valore: (r) => formatEuro(r.prezzoUnitario * r.quantita),
        quota: 17,
        halign: 'right',
      }
    );
  }

  const quotaTotale = colonne.reduce((sum, c) => sum + c.quota, 0);

  const head = [colonne.map((c) => c.header)];
  const body: RowInput[] = ordine.righe.map((r) => colonne.map((c) => c.valore(r)));

  const columnStyles: Record<string, Partial<import('jspdf-autotable').Styles>> = {};
  colonne.forEach((c, index) => {
    columnStyles[index] = {
      cellWidth: (tableWidth * c.quota) / quotaTotale,
      ...(c.halign ? { halign: c.halign } : {}),
    };
  });

  autoTable(doc, {
    head,
    body,
    startY,
    margin: { left: MARGIN, right: MARGIN },
    styles: {
      fontSize: pdf.fontSize,
      cellPadding: 2.5,
      overflow: 'linebreak',
      lineColor: hexToRgb(colors.borderColor),
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: hexToRgb(colors.headerBg),
      textColor: hexToRgb(colors.headerText),
      fontStyle: 'bold',
      fontSize: pdf.headerFontSize,
    },
    alternateRowStyles: { fillColor: hexToRgb(colors.altRowBg) },
    columnStyles,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (doc as any).lastAutoTable?.finalY || startY + 20;
}

/** Totali: importo dell'ordine, oppure conteggio pezzi nella packing list. */
function addTotali(doc: jsPDF, ordine: OrdineStampa, startY: number): number {
  const { colors } = EXPORT_CONFIG;
  const pageWidth = doc.internal.pageSize.getWidth();
  const y = startY + 8;

  const totalePezzi = ordine.righe
    .filter((r) => !r.voceLibera)
    .reduce((sum, r) => sum + (r.quantita || 0), 0);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...hexToRgb(colors.textPrimary));

  if (ordine.conPrezzi) {
    const totale = ordine.righe.reduce((sum, r) => sum + r.prezzoUnitario * r.quantita, 0);
    const label = 'TOTALE ORDINE';
    const value = formatEuro(totale);
    doc.text(label, pageWidth - MARGIN - doc.getTextWidth(label) - 35, y);
    doc.text(value, pageWidth - MARGIN - doc.getTextWidth(value), y);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...hexToRgb(colors.textSecondary));
    doc.text(`Totale pezzi: ${totalePezzi}`, MARGIN, y);
  } else {
    const label = `TOTALE PEZZI: ${totalePezzi}`;
    doc.text(label, pageWidth - MARGIN - doc.getTextWidth(label), y);
  }

  return y + 8;
}

/** Note del documento e spazio per la firma di ricevuta. */
function addPiede(doc: jsPDF, ordine: OrdineStampa, startY: number): void {
  const { colors } = EXPORT_CONFIG;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let y = startY;

  const note = [ordine.note, ordine.azienda?.noteDocumento].filter(Boolean).join('\n');
  if (note) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...hexToRgb(colors.textPrimary));
    doc.text('Note', MARGIN, y);
    y += 4.5;

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...hexToRgb(colors.textSecondary));
    const lines = doc.splitTextToSize(note, pageWidth - MARGIN * 2);
    doc.text(lines, MARGIN, y);
    y += lines.length * 4.2;
  }

  // Firme in fondo alla pagina
  const firmaY = Math.max(y + 18, pageHeight - 32);
  const colWidth = (pageWidth - MARGIN * 2 - 10) / 2;

  doc.setDrawColor(...hexToRgb(colors.borderColor));
  doc.setLineWidth(0.3);
  doc.line(MARGIN, firmaY, MARGIN + colWidth, firmaY);
  doc.line(pageWidth - MARGIN - colWidth, firmaY, pageWidth - MARGIN, firmaY);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...hexToRgb(colors.textSecondary));
  doc.text('Firma del mittente', MARGIN, firmaY + 4);
  doc.text('Firma del destinatario per ricevuta', pageWidth - MARGIN - colWidth, firmaY + 4);

  doc.setFontSize(7);
  doc.text(
    `Documento generato il ${new Date().toLocaleDateString('it-IT')} con ${EXPORT_CONFIG.appName}`,
    MARGIN,
    pageHeight - 10
  );
}

/** Costruisce il PDF dell'ordine. */
export function creaOrdinePdf(ordine: OrdineStampa): jsPDF {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  let y = addIntestazione(doc, ordine.azienda);
  y = addTestataDocumento(doc, ordine, y);
  y = addRighe(doc, ordine, y);
  y = addTotali(doc, ordine, y);
  addPiede(doc, ordine, y);

  return doc;
}

/** Scarica il documento come file PDF. */
export function scaricaOrdinePdf(ordine: OrdineStampa): void {
  const suffisso = ordine.conPrezzi ? '' : '-spedizione';
  const numero = ordine.numeroOrdine.replace(/[^\w-]/g, '_');
  creaOrdinePdf(ordine).save(`ordine-${numero}${suffisso}.pdf`);
}

/** Apre l'anteprima di stampa del documento in una nuova finestra. */
export function stampaOrdinePdf(ordine: OrdineStampa): void {
  const doc = creaOrdinePdf(ordine);
  doc.autoPrint();
  const url = doc.output('bloburl');
  window.open(url, '_blank');
}
